import { DELIVERY_STATUS } from 'components/MemberList/constants';
import { DateUtil } from '@src/packages/utils/dates';

const resolveDeliveryRecordArrays = ({
  viewed = [],
  delivered = [],
  deliveryState = {},
}) => {
  if (deliveryState && Object.keys(deliveryState).length) {
    return {
      viewed: deliveryState.viewed || [],
      delivered: deliveryState.delivered || [],
    };
  }

  return { viewed, delivered };
};

/**
 * Per-user delivery status and timestamp from execution deliver-state arrays.
 *
 * @param {import('./index.d.ts').DeliveryState} state
 * @returns {Record<string, { status: string, createdAtMs: number | null }>}
 */
export const getMemberDeliveryDetailsByUserId = state => {
  const { viewed, delivered } = resolveDeliveryRecordArrays(state);
  const details = {};

  const assignFromRecords = (records, status) => {
    records?.forEach(record => {
      const userId = record?.user?.id;
      if (!userId) return;

      details[userId] = {
        status,
        createdAtMs: DateUtil.getTimestampMs(record?.createdAt),
      };
    });
  };

  assignFromRecords(delivered, DELIVERY_STATUS.DELIVERED);
  assignFromRecords(viewed, DELIVERY_STATUS.SEEN);

  return details;
};

/**
 * Retrieves the delivery states for users based on their viewed and delivered statuses.
 *
 * @param {Array} viewed - The parameters object.
 * @param {Array} delivered - An object representing users who have received the item.
 * @param {Object} deliveryState - An object representing the current {viewed and delivered} state.
 * @returns {Object} An object where keys are user IDs and values are delivery statuses. Ej: { '1': 'seen', '2': 'delivered' }
 */
export const getUserDeliveryState = ({ viewed = [], delivered = [], deliveryState = {} }) => {
  const status = {};

  // NOTE: assign the delivery status based on the delivery, it's important the order of the checking since the last one will be the one that will be assigned
  const checkingState = (usersViewed = [], usersDelivered = []) => {
    if (usersDelivered?.length) {
      usersDelivered?.forEach(record => {
        status[record?.user?.id] = DELIVERY_STATUS.DELIVERED;
      });
    }

    if (usersViewed?.length) {
      usersViewed?.forEach(record => {
        status[record?.user?.id] = DELIVERY_STATUS.SEEN;
      });
    }
  };

  if (Object.keys(deliveryState).length) {
    checkingState(deliveryState.viewed, deliveryState.delivered);
    return status;
  }

  checkingState(viewed, delivered);

  return status;
};

export { formatMemberDeliveryLabel } from './formatMemberDeliveryLabel';

/**
 * Retrieves the delivery state for a user based on their viewed and delivered statuses.
 *
 * @param {string} userId - The user ID.
 * @param {Object} deliveryState - An object representing the current {viewed and delivered} state.
 * @returns {string} The delivery status for the user.
 */
export const getDeliveryStateByUser = (
  userId,
  { viewed = [], delivered = [], deliveryState = {} },
) => {
  const status = getUserDeliveryState({ viewed, delivered, deliveryState });
  return status[userId] || DELIVERY_STATUS.SENT;
};
